import { getOwner } from '@ember/application';
import { waitForPromise } from '@ember/test-waiters';
import DS from 'ember-data';
import Model from '@ember-data/model';
import { isEmpty } from '@ember/utils';
import isDestroyed from 'cio/utils/is-destroyed';
import AdapterRegistry from 'ember-data/types/registries/adapter';

type HTTPVerb = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD';

type APIActionOptions<DataIn, DataOut> = {
  path: string | ((this: Model, data: DataIn) => any);
  type?: HTTPVerb;
  collection?: boolean;
  before?: (this: Model, data: DataIn) => any;
  after?: (this: Model, respData: any) => DataOut;
};

function snapshotFromRecord(model: Model): DS.Snapshot {
  return (model as any)._createSnapshot();
}

export const modelApiAction = <DataIn = Record<string, any>, DataOut = Record<string, any>>({
  path,
  type,
  collection = false,
  before,
  after,
}: APIActionOptions<DataIn, DataOut>) => {
  return async function runMemberAction(this: Model, data: DataIn): Promise<DataOut> {
    // this can happen in tests: http://127.0.0.1:4200/tests?testId=833e4871
    if (isDestroyed(this)) {
      return {} as DataOut;
    }

    if (this == null) {
      throw new Error('memberAction must be used on a model class');
    }

    let modelClass = this.constructor as typeof Model;
    let { modelName } = modelClass;
    let store = getOwner(this).lookup('service:store');
    let adapter = store.adapterFor(modelName as keyof AdapterRegistry);
    let requestType = 'updateRecord';
    let snapshot = snapshotFromRecord(this);

    let baseUrl = adapter.buildURL(modelName, collection ? null : (this as any).id, snapshot, requestType);
    let finalPath = typeof path === 'function' ? path.call(this, data) : path;
    let url = !isEmpty(finalPath) ? `${baseUrl}/${finalPath}` : baseUrl;
    let reqData = data;

    if (before != null) {
      reqData = before.call(this, data);
    }

    let resPromise = adapter.ajax(url, type ?? 'PUT', { data: reqData });
    let res = await waitForPromise(resPromise); // waitForPromise needed for test waiters to register correctly in Ember 4.12 (probably Ember Data adapters change)

    if (after != null && !this.isDestroyed) {
      return after.call(this, res);
    }

    return res;
  };
};
